import React, { CSSProperties, ReactNode, useState } from 'react';
import styles from '../journey-explorer.module.scss';
import { ExplainSection } from '../journey-explain';
import { Dot } from './filter-group';

export interface PopupOption<T extends string> {
	mode: T;
	label: string;
}

export type ExploreAction = 'breakdown' | 'time' | 'customers';

interface MapPopupProps<T extends string> {
	title: string;
	color?: string;
	goalPct: number | null;
	options: PopupOption<T>[];
	actions: PopupOption<ExploreAction>[];
	position: CSSProperties;
	explainPosition?: CSSProperties;
	onChoose: (mode: T) => void;
	onExplore: (action: ExploreAction) => void;
	footer?: ReactNode;
	explain?: () => ExplainSection[];
}

const TONE_CLASS = {
	good: styles.explain_good,
	bad: styles.explain_bad,
	neutral: '',
};

const MapPopup = <T extends string>({
	title,
	color,
	goalPct,
	options,
	actions,
	position,
	explainPosition,
	onChoose,
	onExplore,
	footer,
	explain,
}: MapPopupProps<T>) => {
	const [explaining, setExplaining] = useState(false);

	return (
		<div
			className={`${styles.popup} ${explaining ? styles.popup_explaining : ''}`}
			style={explaining && explainPosition ? explainPosition : position}
			role="dialog"
			aria-label={title}
			onMouseDown={(event) => event.stopPropagation()}
			onClick={(event) => event.stopPropagation()}
		>
			<div className={styles.popup_header}>
				{color && <Dot color={color} />}
				{title}
				{goalPct !== null && <span className={styles.popup_goal}>{goalPct.toFixed(1)}% reached goal</span>}
			</div>
			{explaining && explain ? (
				<>
					<div className={styles.explain_body}>
						{explain().map((section) => (
							<section key={section.title} className={styles.explain_section}>
								<div className={styles.explain_head}>
									{section.title}
									{section.value && (
										<span
											className={`${styles.explain_value} ${
												TONE_CLASS[section.tone ?? 'neutral']
											}`}
										>
											{section.value}
										</span>
									)}
								</div>
								{section.text.map((line) => (
									<p key={line} className={styles.explain_text}>
										{line}
									</p>
								))}
							</section>
						))}
					</div>
					<button
						type="button"
						className={`${styles.popup_option} ${styles.explain_back}`}
						onClick={() => setExplaining(false)}
					>
						‹ Back
					</button>
				</>
			) : (
				<>
					{options.length > 0 && <div className={styles.popup_heading}>Filter customers who</div>}
					{options.map((option) => (
						<button
							key={option.mode}
							type="button"
							className={styles.popup_option}
							onClick={() => onChoose(option.mode)}
						>
							{option.label}
						</button>
					))}
					<div
						className={`${styles.popup_heading} ${options.length > 0 ? styles.popup_heading_divided : ''}`}
					>
						Explore
					</div>
					{actions.map((action) => (
						<button
							key={action.mode}
							type="button"
							className={`${styles.popup_option} ${styles.popup_explore}`}
							onClick={() => onExplore(action.mode)}
						>
							{action.label}
							<span aria-hidden="true">›</span>
						</button>
					))}
					{footer}
					{explain && (
						<button
							type="button"
							className={`${styles.popup_option} ${styles.popup_explore} ${styles.explain_open}`}
							onClick={() => setExplaining(true)}
						>
							Explain these numbers
							<span aria-hidden="true">›</span>
						</button>
					)}
				</>
			)}
		</div>
	);
};

export default MapPopup;
